import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MaskService } from '../../../shared/services/mask.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import {
  PesquisaCodigoTributacaoModalComponent,
  PesquisaCodigoTributacaoModalData,
  PesquisaCodigoTributacaoModalResult,
} from '../../../shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ServicoService } from '../../../shared/services/apis/servico/servico.service';
import { LookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { MotorFiscalLookupService } from '../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { ServicoRequest } from '../../../shared/services/requests/servico/servico.request';
import { CodigoTributacaoService } from '../../../shared/services/apis/servico/codigo-tributacao.service';
import { CodigoTributacaoRequest } from '../../../shared/services/requests/servico/codigo-tributacao.request';
import { LookupItemStruct } from '../../../shared/services/structs/pessoa/lookup-item.struct';
import { UnidadeMedidaStruct } from '../../../shared/services/structs/produto/unidade-medida.struct';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-servico-cadastro',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterActionComponent,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    NgxMaskDirective,
    SelectPesquisavelComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './servico-cadastro.component.html',
  styleUrls: ['./servico-cadastro.component.css'],
})
export class ServicoCadastroComponent implements OnInit {
  // Enums
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Servicos;

  public form: FormGroup;
  public readonly masks = MaskService.getMasks();
  public currencyMask = this.maskService.getDynamicCurrencyMask();
  public isEditMode: boolean = false;
  public idServico: number | null = null;
  public isLoadingSalvar: boolean = false;
  public isLoadingDados: boolean = false;

  // Lookups
  public listaUnidadeMedida: UnidadeMedidaStruct[] | null = null;
  public listaTipoItemSped: LookupItemStruct[] | null = null;

  // Controle de informou manualmente tributação nacional
  private informouTributacaoManualmente: boolean = false;

  // NBS dynamic search function (kept for potential reuse)

  public pesquisarNbs(): void {
    const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '700px',
      maxHeight: '85vh',
      panelClass: 'pesquisa-codigo-tributacao-dialog',
      data: {
        titulo: 'Pesquisar NBS',
        buscarFn: (pesquisa, paginaAtual, tamanhoPagina) => {
          const request = new CodigoTributacaoRequest();
          request.pesquisa = pesquisa;
          request.paginaAtual = paginaAtual;
          request.tamanhoPagina = tamanhoPagina;
          return this.codigoTributacaoService
            .getNbs(request)
            .pipe(
              map(r => ({
                isError: r.isError,
                itens: (r.itens || []).map(i => ({ codigo: i.codigo, descricao: i.descricao })),
              }))
            );
        },
        permitirManual: false,
        registrosIniciais: 10,
        valorInicial: this.form.getRawValue().nbs || undefined,
      } as PesquisaCodigoTributacaoModalData,
    });

    dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
      if (!result) return;
      if (result.selecionado) {
        this.form.patchValue({
          nbs: this.apenasDigitos(result.selecionado.codigo),
        });
      }
    });
  }

  public limparNbs(): void {
    this.form.patchValue({ nbs: '' });
  }

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private servicoService: ServicoService,
    private lookupService: LookupService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private codigoTributacaoService: CodigoTributacaoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private maskService: MaskService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      descricao: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
      codigo: ['', [Validators.maxLength(50)]],
      isAtivo: [true],
      preco: [null],
      idUnidadeMedida: [null],
      idTipoItemSped: [null],
      codigoTributacaoNacionalServico: [''],
      descricaoTributacaoNacionalServico: [''],
      codigoTributacaoNacionalServicoLc116: [{ value: '', disabled: true }],
      codigoMunicipalComplementar: ['', [Validators.maxLength(50)]],
      nbs: [''],
      descricaoComplementar: [''],
      observacao: [''],
    });

    this.carregarLookups();
    this.checkRoute();
  }

  private checkRoute(): void {
    const paramId = this.route.snapshot.paramMap.get('idServico');
    if (paramId) {
      this.isEditMode = true;
      this.idServico = Number(paramId);
      this.carregarServico();
    }
  }

  private carregarLookups(): void {
    this.lookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaUnidadeMedida = res.listaUnidadeMedida || [];
        }
      },
    });

    // Tipo de Item SPED é obtido de db_motor_fiscal via API MotorFiscal.
    this.motorFiscalLookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaTipoItemSped = res.listaTipoItemSped || [];
        }
      },
    });
  }

  private carregarServico(): void {
    if (!this.idServico) return;
    this.isLoadingDados = true;

    this.servicoService.getById(this.idServico).subscribe({
      next: res => {
        this.isLoadingDados = false;
        if (res.isError) {
          this.alertService.show('Atenção', res.errorDescription, AlertType.warning);
          this.router.navigate(['/cadastros/servicos']);
          return;
        }
        this.form.patchValue({
          descricao: res.descricao,
          codigo: res.codigo,
          isAtivo: res.isAtivo,
          preco: res.preco,
          idUnidadeMedida: res.idUnidadeMedida,
          idTipoItemSped: res.idTipoItemSped,
          codigoTributacaoNacionalServico: res.codigoTributacaoNacionalServico,
          descricaoTributacaoNacionalServico: res.descricaoTributacaoNacionalServico,
          codigoTributacaoNacionalServicoLc116: res.codigoTributacaoNacionalServicoLc116,
          codigoMunicipalComplementar: res.codigoMunicipalComplementar,
          nbs: res.nbs,
          descricaoComplementar: res.descricaoComplementar,
          observacao: res.observacao,
        });
        this.informouTributacaoManualmente = res.informouCodigoTributacaoNacionalServicoManualmente ?? false;
      },
      error: () => {
        this.isLoadingDados = false;
        this.alertService.show('Erro', 'Falha ao carregar serviço.', AlertType.error);
        this.router.navigate(['/cadastros/servicos']);
      },
    });
  }

  // --- Catálogo Modals ---

  public pesquisarTributacaoNacional(): void {
    const formValue = this.form.getRawValue();
    const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '700px',
      maxHeight: '85vh',
      panelClass: 'pesquisa-codigo-tributacao-dialog',
      data: {
        titulo: 'Pesquisar Código de Tributação Nacional',
        buscarFn: (pesquisa, paginaAtual, tamanhoPagina) => {
          const request = new CodigoTributacaoRequest();
          request.pesquisa = pesquisa;
          request.paginaAtual = paginaAtual;
          request.tamanhoPagina = tamanhoPagina;
          return this.codigoTributacaoService
            .getTributacaoNacional(request)
            .pipe(
              map(r => ({
                isError: r.isError,
                itens: (r.itens || []).map(i => ({ codigo: i.codigo, descricao: i.descricao })),
              }))
            );
        },
        permitirManual: true,
        permitirDescricaoManual: true,
        registrosIniciais: 10,
        iniciarManual: this.informouTributacaoManualmente,
        codigoManualInicial: this.informouTributacaoManualmente
          ? formValue.codigoTributacaoNacionalServico
          : undefined,
        descricaoManualInicial: this.informouTributacaoManualmente
          ? formValue.descricaoTributacaoNacionalServico
          : undefined,
        valorInicial: !this.informouTributacaoManualmente
          ? formValue.codigoTributacaoNacionalServico || undefined
          : undefined,
      } as PesquisaCodigoTributacaoModalData,
    });

    dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
      if (!result) return;

      if (result.manual) {
        this.informouTributacaoManualmente = true;
        this.form.patchValue({
          codigoTributacaoNacionalServico: this.apenasDigitos(result.codigoManual),
          descricaoTributacaoNacionalServico: result.descricaoManual,
        });
        this.derivarLc116(result.codigoManual || '');
      } else if (result.selecionado) {
        this.informouTributacaoManualmente = false;
        this.form.patchValue({
          codigoTributacaoNacionalServico: this.apenasDigitos(result.selecionado.codigo),
          descricaoTributacaoNacionalServico: result.selecionado.descricao,
        });
        this.derivarLc116(result.selecionado.codigo);
      }
    });
  }

  public limparTributacaoNacional(): void {
    this.informouTributacaoManualmente = false;
    this.form.patchValue({
      codigoTributacaoNacionalServico: '',
      descricaoTributacaoNacionalServico: '',
      codigoTributacaoNacionalServicoLc116: '',
    });
  }

  /**
   * Derives the LC116 code from the tributação nacional code,
   * but only fills the field if the code actually exists in the LC116 table.
   */
  private derivarLc116(codigoTributacao: string): void {
    if (!codigoTributacao) return;
    const digits = codigoTributacao.replace(/\D/g, '');
    if (digits.length < 4) return;

    const lc116Candidato = digits.substring(0, 4);

    const request = new CodigoTributacaoRequest();
    request.pesquisa = lc116Candidato;
    request.paginaAtual = 1;
    request.tamanhoPagina = 50;

    this.codigoTributacaoService.getLc116(request).subscribe({
      next: response => {
        if (response.isError || !response.itens || response.itens.length === 0) {
          this.form.patchValue({ codigoTributacaoNacionalServicoLc116: '' });
          this.alertService.show(
            'Informação',
            'Não foi encontrado nenhum código LC 116 correspondente ao código de tributação informado.',
            AlertType.info
          );
          return;
        }
        // Verifica se algum item retornado possui exatamente o código candidato (apenas dígitos)
        const encontrado = response.itens.find(item => {
          const itemDigits = item.codigo.replace(/\D/g, '');
          return itemDigits === lc116Candidato;
        });
        if (encontrado) {
          this.form.patchValue({ codigoTributacaoNacionalServicoLc116: lc116Candidato });
        } else {
          this.form.patchValue({ codigoTributacaoNacionalServicoLc116: '' });
          this.alertService.show(
            'Informação',
            'Não foi encontrado nenhum código LC 116 correspondente ao código de tributação informado.',
            AlertType.info
          );
        }
      },
      error: () => {
        this.form.patchValue({ codigoTributacaoNacionalServicoLc116: '' });
      },
    });
  }

  public limparLc116(): void {
    this.form.patchValue({ codigoTributacaoNacionalServicoLc116: '' });
  }

  public resyncLc116(): void {
    const codigoTrib = this.form.get('codigoTributacaoNacionalServico')?.value;
    if (codigoTrib) {
      this.derivarLc116(codigoTrib);
    }
  }

  /** Bloqueia caracteres não numéricos. O tamanho e o ponto são controlados pelo ngx-mask. */
  public onCodigoKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    const isShortcut = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (allowed.includes(event.key) || isShortcut) return;
    if (!/^\d$/.test(event.key)) { event.preventDefault(); }
  }

  /** Ao sair do campo de código de tributação nacional (digitado), infere o LC 116 (primeiros 4 dígitos). */
  public onCodigoTribNacionalBlur(): void {
    const digits = this.apenasDigitos(this.form.get('codigoTributacaoNacionalServico')?.value);
    if (digits.length === 6) {
      this.informouTributacaoManualmente = true;
      this.derivarLc116(digits);
    }
  }

  // --- Save ---

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isLoadingSalvar = true;
    const request = this.montarRequest();

    if (this.isEditMode && this.idServico) {
      this.servicoService.put(this.idServico, request).subscribe({
        next: res => {
          this.isLoadingSalvar = false;
          if (res.isError) {
            this.alertService.show('Atenção', res.errorDescription, AlertType.warning);
            return;
          }
          this.alertService.show('Sucesso', 'Serviço atualizado com sucesso.', AlertType.success);
          this.router.navigate(['/cadastros/servicos']);
        },
        error: () => {
          this.isLoadingSalvar = false;
          this.alertService.show('Erro', 'Falha ao atualizar serviço.', AlertType.error);
        },
      });
    } else {
      this.servicoService.post(request).subscribe({
        next: res => {
          this.isLoadingSalvar = false;
          if (res.isError) {
            this.alertService.show('Atenção', res.errorDescription, AlertType.warning);
            return;
          }
          this.alertService.show('Sucesso', 'Serviço cadastrado com sucesso.', AlertType.success);
          this.router.navigate(['/cadastros/servicos']);
        },
        error: () => {
          this.isLoadingSalvar = false;
          this.alertService.show('Erro', 'Falha ao cadastrar serviço.', AlertType.error);
        },
      });
    }
  }

  private montarRequest(): ServicoRequest {
    const formValue = this.form.getRawValue();
    const request = new ServicoRequest();
    request.descricao = formValue.descricao;
    request.codigo = formValue.codigo || undefined;
    request.isAtivo = formValue.isAtivo;
    request.preco = formValue.preco || 0;
    request.idUnidadeMedida = formValue.idUnidadeMedida || undefined;
    request.idTipoItemSped = formValue.idTipoItemSped || undefined;
    // Armazena apenas dígitos (sem pontuação)
    request.codigoTributacaoNacionalServico =
      this.apenasDigitos(formValue.codigoTributacaoNacionalServico) || undefined;
    request.descricaoTributacaoNacionalServico = formValue.descricaoTributacaoNacionalServico || undefined;
    request.codigoTributacaoNacionalServicoLc116 =
      this.apenasDigitos(formValue.codigoTributacaoNacionalServicoLc116) || undefined;
    request.codigoMunicipalComplementar = formValue.codigoMunicipalComplementar || undefined;
    request.nbs = this.apenasDigitos(formValue.nbs) || undefined;
    request.descricaoComplementar = formValue.descricaoComplementar || undefined;
    request.observacao = formValue.observacao || undefined;
    request.informouCodigoTributacaoNacionalServicoManualmente = this.informouTributacaoManualmente;
    return request;
  }

  /** Remove qualquer caractere que não seja dígito */
  private apenasDigitos(valor: string | null | undefined): string {
    if (!valor) return '';
    return valor.replace(/\D/g, '');
  }

  public cancelar(): void {
    this.router.navigate(['/cadastros/servicos']);
  }
}
